import { useAuth } from "@/context/AuthContext";

// ---------------------------------------------------------------------------
// Feature-level (in-component) RBAC helpers — buttons, cards, actions, tabs.
//
// NOTE (P51): the old ROUTE_ROLES map / rolesForPath() that mirrored App.js route
// guards for the sidebar has been REMOVED. Navigation visibility is now driven
// entirely by the server (GET /me/navigation, P50) — a single source of truth.
// App.js `requireRole`/`ProtectedRoute` remain the defense-in-depth ACCESS layer.
// These helpers below are only for gating UI *within* an already-authorized page.
// ---------------------------------------------------------------------------

const MGR = ["superadmin", "admin", "manager"];
const SA = ["superadmin", "admin"];

// Pure check: does `role` satisfy the `roles` allow-list (undefined/null => all).
export function roleAllowed(role, roles) {
  return !roles || roles.length === 0 || roles.includes(role);
}

// Hook: does the current user have any of the given roles? (no roles => true)
export function useHasRole(roles) {
  const { user } = useAuth();
  return roleAllowed(user?.role, roles);
}

// Declarative guard for feature-level UI (buttons, cards, actions).
// Renders children only when the current user's role is allowed; otherwise `fallback`.
export function RequireRole({ roles, children, fallback = null }) {
  const ok = useHasRole(roles);
  return ok ? children : fallback;
}

// Convenience role groups for feature-level gating across pages.
export const ROLE_GROUPS = { MGR, SA, MGR_CASHIER: [...MGR, "cashier"] };

// ---------------------------------------------------------------------------
// Permission-aware gating (fine-grained, "exactly the actions you can perform").
// Backed by the user's effective permission set from /api/security/me/permissions.
// Use for action buttons inside hubs (approve / execute / post / configure …).
// ---------------------------------------------------------------------------

// Hook: does the current user hold the given permission code (MODULE.ACTION)?
export function useHasPermission(code) {
  const { hasPermission } = useAuth();
  return hasPermission(code);
}

// Declarative guard. `perm` = single code, or `anyOf` = array (user needs ANY).
// Renders children only when authorized; otherwise renders `fallback`.
export function RequirePermission({ perm, anyOf, children, fallback = null }) {
  const { hasPermission } = useAuth();
  const codes = anyOf || (perm ? [perm] : []);
  const ok = codes.length === 0 ? true : codes.some((c) => hasPermission(c));
  return ok ? children : fallback;
}
